/**
 * Pieces shared by `<FormDialog>` and `<FormSheet>`.
 *
 * The two differ only in chrome: one is a centre dialog, the other a right-side
 * sheet. Everything else — the slot contract, the footer's action shape and its
 * loading lock, the notistack dismissal guard — is identical, so it lives here
 * once. Sharing the slot COMPONENTS (not just their markup) also means slot
 * extraction by reference identity works across both shells, and a consumer
 * that swaps `FormDialog` for `FormSheet` changes only the chrome it names.
 */
import { useContext } from 'react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { mergeClasses } from '@/shared/lib/merge-classes';
import { FormShellBodyInsetContext } from './formShell.context';
import type { FormDialogBodyProps, FormDialogFooterProps } from './FormDialog.types';

/**
 * Body slot. Pure container; anything goes inside. Its inset comes from the shell holding it
 * (`FormShellBodyInsetContext`), and a padding in `className` replaces it.
 *
 * Identity is what slot extraction matches against; do NOT rename or alias
 * this component without updating `findSlots`.
 */
export const FormShellBody = ({ children, className }: FormDialogBodyProps) => {
  const inset = useContext(FormShellBodyInsetContext);
  return (
    <div data-slot="form-shell-body" className={mergeClasses(inset, className)}>
      {children}
    </div>
  );
};

/**
 * Footer slot. Renders 0–2 actions (primary + secondary) and enforces the
 * "at least one action" contract. Loading on the primary locks BOTH buttons
 * (single source of truth).
 *
 * Throws synchronously when neither action is provided so the mistake
 * surfaces deterministically in tests AND in development — silent empty
 * footers are a bug, not a feature.
 */
export const FormShellFooter = ({ primary, secondary, leading }: FormDialogFooterProps) => {
  if (!primary && !secondary) {
    throw new Error(
      'FormDialog.Footer: at least one of `primary` or `secondary` must be provided.',
    );
  }

  // Loading on primary is the lock. We OR-merge per-action `disabled` with
  // the lock so consumers cannot accidentally re-enable a button while a
  // submit is in flight.
  const isLocked = primary?.loading === true;

  return (
    <>
      {/* `mr-auto` rather than a spacer: the footer rows are `justify-end`, so this is what puts
          one child at the other end without every consumer having to lay the row out itself. */}
      {leading !== undefined && <div className="mr-auto min-w-0">{leading}</div>}
      {secondary && (
        <Button
          variant="outline"
          data-testid={secondary['data-testid']}
          onClick={secondary.onClick}
          disabled={(secondary.disabled ?? false) || isLocked}
        >
          {secondary.label}
        </Button>
      )}
      {primary && (
        <Button
          data-testid={primary['data-testid']}
          onClick={primary.onClick}
          disabled={(primary.disabled ?? false) || isLocked}
        >
          {isLocked && <Spinner />}
          {primary.label}
        </Button>
      )}
    </>
  );
};
